import { format } from "date-fns";
import { CalendarCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { getSessionsQueryKey } from "@/features/sessions/api/get-sessions";
import { queryClient } from "@/lib/react-query";
import { getDateWithTime } from "@/lib/utils";
import { HttpError, isBadRequest } from "@/types/error";
import {
  PublicSession,
  RecurringEndType,
  RecurringFrequency,
} from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreateSession } from "../api/add-session";
import { getSessionQueryKey } from "../api/get-session";
import { useUpdateSession } from "../api/update-session";
import {
  AddSessionFormData,
  SessionVisibility,
  addSessionSchema,
} from "../types/add-session-schema";
import {
  transformApiDataToFormData,
  transformFormDataToApiData,
} from "../utils/session-data-transformation";

import { AttendeesListElement } from "./attendees-list.element";
import { AttendeesSearchElement } from "./attendees.element";
import { OptionalSettingsElement } from "./optional-settings.element";
import { OrganisationsListElement } from "./organisations-list.element";
import { OrganisationsSearchElement } from "./organisations.element";
import { RecurringSettingsElement } from "./recurring-settings.element";
import { RoomSystemsListElement } from "./room-systems-list.element";
import { RoomSystemsSearchElement } from "./room-systems.element";
import { SessionDetailsElement } from "./session-details.element";
import { UpdateRecurringSessionModal } from "./update-recurring-session-modal";
import { VisibilityElement } from "./visibility.element";

type SessionFormProps = {
  initialValues?: PublicSession;
};

export function SessionForm({ initialValues }: SessionFormProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organisation } = useOrganisation();
  const { data: user, isSuccess: userIsSuccess } = useGetAuthUser();
  const isEdit = !!initialValues;
  const [
    isUpdateRecurringSessionModalOpen,
    setIsUpdateRecurringSessionModalOpen,
  ] = useState<AddSessionFormData | undefined>(undefined);

  const {
    mutateAsync: createSession,
    isError: createSessionIsError,
    error: createSessionError,
  } = useCreateSession({
    onSuccess: async (session) => {
      queryClient.setQueryData<PublicSession>(
        getSessionQueryKey({ orgId: organisation.id, sessionId: session.id }),
        session,
      );

      await queryClient.invalidateQueries({
        queryKey: getSessionsQueryKey({ organisationId: organisation.id }),
      });

      navigate(`/sessions/${session.id}`);
    },
  });

  const {
    mutateAsync: updateSession,
    isError: updateSessionIsError,
    error: updateSessionError,
  } = useUpdateSession({
    onSuccess: async (session) => {
      form.reset(transformApiDataToFormData(session));
      queryClient.setQueryData<PublicSession>(
        getSessionQueryKey({ orgId: organisation.id, sessionId: session.id }),
        session,
      );
    },
  });

  const form = useForm<AddSessionFormData>({
    resolver: zodResolver(addSessionSchema),
    defaultValues: transformApiDataToFormData(
      initialValues,
      searchParams.get("date") ?? undefined,
    ),
  });

  async function handleCreateSession(data: AddSessionFormData) {
    const from = getDateWithTime(data.startDate, data.startTime);
    const to = getDateWithTime(data.startDate, data.endTime);

    try {
      await createSession({
        name: data.name,
        description: data.description,
        start: from.toISOString(),
        end: to.toISOString(),
        guests: data.guests?.map((guest) => guest.email) ?? [],
        moderatorIds:
          data.guests
            .filter((guest) => guest.isModerator)
            .map((guest) => guest.userId)
            .filter((id) => id !== undefined) ?? [],
        isPrivate: data.visibility === SessionVisibility.PRIVATE,
        isConfidential: data.isConfidential,
        isScheduledToRecord: data.isScheduledToRecord,
        isParticipantScreenSharing: data.isParticipantScreenSharing,
        isClosedCaptionsEnabled: data.isClosedCaptionsEnabled,
        roomSystemIds:
          data.roomSystems?.map((roomSystem) => roomSystem.id) ?? [],
        recurringFrequency: data.isRecurring
          ? data.recurringFrequency
          : undefined,
        weeklyDays:
          data.isRecurring &&
          data.recurringFrequency === RecurringFrequency.WEEKLY
            ? data.weeklyDays
            : undefined,
        weeklyInterval:
          data.isRecurring &&
          data.recurringFrequency === RecurringFrequency.WEEKLY
            ? data.weeklyInterval
            : undefined,
        endDateRecurring:
          data.endType === RecurringEndType.DATE &&
          data.isRecurring &&
          data.endDate
            ? format(data.endDate, "yyyy-MM-dd")
            : undefined,
        nbRepetitions:
          data.endType === RecurringEndType.NB_REPETITIONS && data.isRecurring
            ? data.nbRepetitions
            : undefined,
        organisationIds: data.invitedOrganisations?.map(
          (organisation) => organisation.id,
        ),
        recurringEndType: data.isRecurring ? data.endType : undefined,
        isRaisedHandSoundEnabled: data.isRaisedHandSoundEnabled,
        isChatEnabled: data.isChatEnabled,
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        const errors = httpError.message;
        errors.forEach((error) => {
          if (error.includes("weeklyDays")) {
            form.setError("weeklyDays", {
              message: error.replace("weeklyDays ", ""),
            });
          }
        });

        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  //add the current user as a guest if the session is not being edited
  useEffect(() => {
    if (isEdit || !userIsSuccess) {
      return;
    }

    form.setValue("guests", [
      {
        userId: user.sub,
        email: user.email?.toLowerCase().trim() ?? "",
        firstName: user.given_name ?? "",
        lastName: user.family_name ?? "",
        isExternal: false,
        isHost: true,
      },
    ]);
  }, [userIsSuccess, form, isEdit, user]);

  async function handleUpdateSession(data: AddSessionFormData) {
    if (!isEdit) {
      // cant happen, its here for type safety
      return;
    }

    // if its recurring, ask user if they want to update all sessions or just one
    if (data.isRecurring) {
      setIsUpdateRecurringSessionModalOpen(data);
      return;
    }

    // if its not recurring, update the session in place
    await updateSession({
      id: initialValues.id,
      session: transformFormDataToApiData(data),
    });
  }

  const isError = createSessionIsError || updateSessionIsError;
  const error = createSessionError || updateSessionError;

  return (
    <>
      {initialValues?.recurringDetails?.id && (
        <UpdateRecurringSessionModal
          sessionId={initialValues?.id}
          recurringId={initialValues?.recurringDetails.id}
          isOpen={!!isUpdateRecurringSessionModalOpen}
          data={isUpdateRecurringSessionModalOpen}
          onClose={() => setIsUpdateRecurringSessionModalOpen(undefined)}
        />
      )}
      <Form {...form}>
        <form
          onSubmit={
            isEdit
              ? form.handleSubmit(handleUpdateSession)
              : form.handleSubmit(handleCreateSession)
          }
        >
          <div className="gap-4 space-y-4 md:grid md:grid-cols-12 md:space-y-0">
            <div className="col-span-7 space-y-4">
              <Card>
                <Card.Header
                  title="Session Details"
                  description="Configure your session"
                />
                <Card.Content>
                  <SessionDetailsElement />
                </Card.Content>
              </Card>

              <Card>
                <Card.Header title="Repeating Session?" />
                <Card.Content>
                  <RecurringSettingsElement />
                </Card.Content>
              </Card>

              <Card>
                <Card.Header
                  title="Visibility"
                  description="Who will be able to discover this session?"
                />
                <Card.Content>
                  <VisibilityElement />
                </Card.Content>
              </Card>
              <Card>
                <Card.Header title="Optional Settings" />
                <Card.Content>
                  <OptionalSettingsElement />
                </Card.Content>
              </Card>
            </div>
            <div className="col-span-5 space-y-4">
              <Card>
                <Card.Header title="Invite Attendees" />
                <Card.Content>
                  <AttendeesSearchElement />
                  <AttendeesListElement />
                </Card.Content>
              </Card>

              <Card>
                <Card.Header title="Add Room System" />
                <Card.Content>
                  <RoomSystemsSearchElement />
                  <RoomSystemsListElement />
                </Card.Content>
              </Card>

              <Card>
                <Card.Header title="Invite External Organisation" />
                <Card.Content>
                  <OrganisationsSearchElement />
                  <OrganisationsListElement />
                </Card.Content>
              </Card>
            </div>
          </div>
          <div className="mt-4 gap-4 space-y-4 md:grid md:grid-cols-12 md:space-y-0">
            <div className="col-span-7 flex flex-col space-y-4">
              {isError && <ErrorMessage error={error} />}
              <Button
                type="submit"
                className="ml-auto"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                <CalendarCheck className="mr-1 size-4 text-inherit" />
                {isEdit ? "Update Session" : "Schedule Session"}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
}
